import {matchPath} from 'react-router-dom';

export type RouteMatchRowType<T> = {
    pattern: string;
    chain: Array<T>;
};

/** Higher score = match before others (more static segments, longer path). */
function routePatternSpecificity(pattern: string): number {
    const parts = pattern.split('/').filter(Boolean);
    const base = parts.length * 100;

    return (
        base +
        parts.reduce((accumulator, part) => {
            if (part.startsWith(':')) {
                return accumulator - 5;
            }

            if (part === '*') {
                return accumulator - 50;
            }

            return accumulator + 25;
        }, 0)
    );
}

type PartitionedRoutesType<T> = {
    pathnameLiteralHit: Array<T> | null;
    exactStatic: Map<string, Array<T>>;
    parameterPatterns: Array<{pattern: string; chain: Array<T>}>;
    staticPrefixRows: Array<{pattern: string; chain: Array<T>}>;
};

function partitionRouteRowsForPathname<T>(
    rows: Array<RouteMatchRowType<T>>,
    pathname: string
): PartitionedRoutesType<T> {
    return rows.reduce<PartitionedRoutesType<T>>(
        (accumulator, {pattern, chain}) => {
            if (!pattern) {
                return accumulator;
            }

            if (pattern === pathname) {
                if (accumulator.pathnameLiteralHit) {
                    return accumulator;
                }

                return {
                    ...accumulator,
                    pathnameLiteralHit: chain,
                };
            }

            if (!pattern.startsWith('/')) {
                return accumulator;
            }

            if (pattern.includes(':')) {
                return {
                    ...accumulator,
                    parameterPatterns: [...accumulator.parameterPatterns, {pattern, chain}],
                };
            }

            const nextExactStatic = accumulator.exactStatic.has(pattern)
                ? accumulator.exactStatic
                : new Map(accumulator.exactStatic).set(pattern, chain);

            return {
                ...accumulator,
                exactStatic: nextExactStatic,
                staticPrefixRows: [...accumulator.staticPrefixRows, {pattern, chain}],
            };
        },
        {
            pathnameLiteralHit: null,
            exactStatic: new Map(),
            parameterPatterns: [],
            staticPrefixRows: [],
        }
    );
}

function firstMatchingParameterPattern<T>(
    pathname: string,
    parameterPatterns: Array<{pattern: string; chain: Array<T>}>,
    exact: boolean
): Array<T> | null {
    const sorted = [...parameterPatterns].sort(
        (left, right) => routePatternSpecificity(right.pattern) - routePatternSpecificity(left.pattern)
    );

    const found = sorted.find((row) => matchPath(pathname, {exact, path: row.pattern}));

    return found ? found.chain : null;
}

function firstMatchingStaticPrefix<T>(
    pathname: string,
    staticPrefixRows: Array<{pattern: string; chain: Array<T>}>
): Array<T> | null {
    const byPattern = staticPrefixRows.reduce<Map<string, Array<T>>>((map, row) => {
        if (!map.has(row.pattern)) {
            map.set(row.pattern, row.chain);
        }

        return map;
    }, new Map());

    const sortedPatterns = [...byPattern.keys()].sort((left, right) => right.length - left.length);

    const matchedPattern = sortedPatterns.find((pattern) => matchPath(pathname, {exact: false, path: pattern}));

    if (!matchedPattern) {
        return null;
    }

    const chainForMatch = byPattern.get(matchedPattern);

    return chainForMatch ?? null;
}

/**
 * Same resolution order as Ant Design menu selected keys: literal pathname, exact static,
 * param patterns (exact then prefix), then longest static prefix via matchPath.
 */
export function resolveChainForPathname<T>(pathname: string, rows: Array<RouteMatchRowType<T>>): Array<T> {
    if (!pathname) {
        return [];
    }

    const partitioned = partitionRouteRowsForPathname(rows, pathname);

    if (partitioned.pathnameLiteralHit) {
        return partitioned.pathnameLiteralHit;
    }

    const exactStaticHit = partitioned.exactStatic.get(pathname);

    if (exactStaticHit) {
        return exactStaticHit;
    }

    return (
        firstMatchingParameterPattern(pathname, partitioned.parameterPatterns, true) ??
        firstMatchingParameterPattern(pathname, partitioned.parameterPatterns, false) ??
        firstMatchingStaticPrefix(pathname, partitioned.staticPrefixRows) ??
        []
    );
}
